import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Pagination, Spin, message } from 'antd';

import * as actions from '../../actions/customersActions';
import { RootState } from '../../reducers/store';
import { useClient } from 'src/contexts';
import { filterApiResponse } from './utils';
import { TableCustomerListType } from './types';
import { SortKey } from './Customers';
import CustomerTile from './CustomerTile';

import styles from './Customers.module.css';

export const CUSTOMERS_PER_PAGE = 18;

const sortCustomers = (
  customers: TableCustomerListType,
  sortKey: SortKey
): TableCustomerListType => {
  const sorted = [...customers];
  switch (sortKey) {
    case 'recent':
      return sorted;
    case 'oldest':
      return sorted.reverse();
    case 'alpha':
      return sorted.sort((a, b) =>
        a.customer.name.localeCompare(b.customer.name, 'fr', {
          sensitivity: 'base',
        })
      );
    default:
      return sorted;
  }
};

const CustomerList: React.FC<{
  searchTerm: string;
  typeFilter: string[];
  sortKey: SortKey;
  customers: TableCustomerListType;
  customersTotal: number;
  listCustomers: (searchTerm: string, page?: number) => void;
  deleteCustomer: (
    id: string,
    successCallback: () => void,
    failureCallback: () => void
  ) => void;
}> = ({
  searchTerm,
  typeFilter,
  sortKey,
  customers,
  customersTotal,
  listCustomers,
  deleteCustomer,
}) => {
  const { patch } = useClient();
  const [current, setCurrent] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  // local override of customer status to reflect optimistic updates
  // without waiting for a full refresh
  const [statusOverrides, setStatusOverrides] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    setLoading(true);
    listCustomers(searchTerm, current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, current]);

  useEffect(() => {
    setLoading(false);
  }, [customers]);

  useEffect(() => {
    setCurrent(1);
  }, [searchTerm, typeFilter]);

  // Apply type filter + sort + status override
  const visibleCustomers = useMemo(() => {
    const enriched = customers.map(row => ({
      ...row,
      customer: {
        ...row.customer,
        status: statusOverrides[row.customer.id] ?? row.customer.status,
      },
    }));

    const filtered =
      typeFilter.length === 0
        ? enriched
        : enriched.filter(row => typeFilter.includes(row.customer.type));

    return sortCustomers(filtered, sortKey);
  }, [customers, typeFilter, sortKey, statusOverrides]);

  const handleStatusChange = (id: string, active: boolean) => {
    const status = active ? 'active' : 'inactive';
    setStatusOverrides(prev => ({ ...prev, [id]: status }));
    patch(
      `/customers/${id}`,
      { status },
      () => {
        message.success(
          `Le client a bien été ${active ? 'activé' : 'désactivé'}.`
        );
      },
      () => {
        // rollback
        setStatusOverrides(prev => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        message.error(
          `Une erreur s'est produite lors de ${
            active ? "l'activation" : 'la désactivation'
          }.`
        );
      }
    );
  };

  const handleDelete = (id: string) => {
    deleteCustomer(
      id,
      () => message.success('Le client a bien été supprimé'),
      () =>
        message.error(
          "Une erreur s'est produite lors de la suppression du client"
        )
    );
  };

  if (loading && customers.length === 0) {
    return (
      <div className={styles.empty}>
        <Spin />
      </div>
    );
  }

  if (!loading && visibleCustomers.length === 0) {
    return (
      <div className={styles.empty}>
        <h3>Aucun client trouvé</h3>
        <p>Essayez de modifier vos filtres ou votre recherche.</p>
      </div>
    );
  }

  return (
    <>
      <div className={styles.resultCount}>
        <strong>{customersTotal.toLocaleString('fr-FR')}</strong> client
        {customersTotal > 1 ? 's' : ''}
      </div>

      <div className={styles.grid}>
        {visibleCustomers.map(row => (
          <CustomerTile
            key={row.key}
            customer={row.customer}
            onStatusChange={handleStatusChange}
            onDelete={handleDelete}
          />
        ))}
      </div>

      <div className={styles.paginationWrapper}>
        <Pagination
          current={current}
          total={customersTotal}
          pageSize={CUSTOMERS_PER_PAGE}
          showSizeChanger={false}
          onChange={page => setCurrent(page)}
        />
      </div>
    </>
  );
};

const mapStateToProps = ({ customers }: RootState) => ({
  customers: filterApiResponse(customers.items),
  customersTotal: customers.total,
});

export default connect(mapStateToProps, actions)(CustomerList);
